/**
 * useIndustry — current active business's industry config.
 * useT        — terminology helper. `t('products')` → "Menu" for restaurants.
 * useSetIndustry — mutation for changing a business's industry.
 *
 * v2 (Day 14.5): replaced businessStore.refetchBusinesses (doesn't exist)
 * with React Query cache invalidation.
 *
 * v3 (Block C0 industry-refresh-fix, Jun 20): resolution no longer flashes to
 * "retail" on a cold refresh — reads persisted activeBusinessIndustryType as
 * the refresh-safe fallback, reconciles to the list value once it loads.
 *
 * v4 (Block C1c LIVE-refresh-fix, Jun 21): useSetIndustry.onSuccess now writes
 * the new industry straight into businessStore (applyIndustryChange) so
 * useIndustry re-derives immediately — no logout/login. The bug: onSuccess
 * only invalidated React Query caches, but useIndustry reads from the Zustand
 * store, NOT React Query, so the screen kept the old industry until a re-login
 * rebuilt the store. The query invalidations remain as the authoritative
 * server reconcile.
 */
import { useMemo, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import useBusinessStore from "@/stores/businessStore";
import {
  INDUSTRIES,
  DEFAULT_INDUSTRY,
  getIndustry,
  type IndustryConfig,
  type IndustryId,
  type IndustryTerms,
} from "@/constants/industries";
import industryService from "@/services/industryService";
import { toast } from "sonner";

/**
 * Only the string-valued keys of IndustryTerms. Excludes nested-object terms
 * like `schedule` (resolved directly via terms.schedule.title, not through t()).
 * The `-?` strips the optional modifier so `undefined` doesn't leak into the
 * key union (homomorphic mapped types copy `?`).
 */
export type StringTermKey = {
  [K in keyof IndustryTerms]-?: IndustryTerms[K] extends string ? K : never;
}[keyof IndustryTerms];

/**
 * 12a: the business's own words (its type of business, then what it set in
 * Settings → Business) on top of the industry's. A pharmaceutical
 * wholesaler says "Customers" and "Sales", not "Patients" and "Dispensary".
 */
function withProfileTerms(config: IndustryConfig, business?: { profile?: { terms?: Record<string, string | undefined> } } | null): IndustryConfig {
  const own = business?.profile?.terms;
  if (!own || Object.keys(own).length === 0) return config;
  const terms = { ...config.terms };
  for (const [k, v] of Object.entries(own)) {
    if (typeof v === "string" && v.trim() && k in terms && typeof (terms as Record<string, unknown>)[k] === "string") {
      (terms as Record<string, unknown>)[k] = v;
    }
  }
  return { ...config, terms };
}

/**
 * Current industry config for the active business.
 *
 * Resolution order (first hit wins):
 *   1. Active business found in availableBusinesses → its industryType
 *      (authoritative; reconciles once the list has loaded).
 *   2. Persisted activeBusinessIndustryType — the refresh-safe snapshot.
 *   3. DEFAULT_INDUSTRY ("retail") — genuinely unknown / Group Mode.
 */
export function useIndustry(): IndustryConfig {
  const activeBusinessSlug = useBusinessStore((s) => s.activeBusinessSlug);
  const availableBusinesses = useBusinessStore((s) => s.availableBusinesses);
  const persistedIndustryType = useBusinessStore(
    (s) => s.activeBusinessIndustryType,
  );
  const pageBusinessId = useBusinessStore((s) => s.pageBusinessId);

  return useMemo(() => {
    // All businesses mode: a page showing one business speaks its language.
    if (!activeBusinessSlug && pageBusinessId) {
      const pb = availableBusinesses.find((b) => b._id === pageBusinessId || b.id === pageBusinessId);
      return pb?.industryType ? withProfileTerms(getIndustry(pb.industryType), pb) : INDUSTRIES[DEFAULT_INDUSTRY];
    }
    // Group Mode — no active business selected.
    if (!activeBusinessSlug) return INDUSTRIES[DEFAULT_INDUSTRY];

    // 1. Authoritative: resolve from the loaded list when present.
    const active = availableBusinesses.find(
      (b) => b.slug === activeBusinessSlug,
    );
    const fromList = active?.industryType;
    if (fromList) return withProfileTerms(getIndustry(fromList), active);

    // 2. Refresh path: list not loaded yet — use the persisted snapshot.
    if (persistedIndustryType) return getIndustry(persistedIndustryType);

    // 3. Genuinely unknown.
    return INDUSTRIES[DEFAULT_INDUSTRY];
  }, [activeBusinessSlug, availableBusinesses, persistedIndustryType, pageBusinessId]);
}

/**
 * Terminology helper. Usage:
 *   const { t, industry } = useT();
 *   <h1>{t('products')}</h1>
 */
export function useT() {
  const industry = useIndustry();
  const t = useCallback(
    (key: StringTermKey): string => industry.terms[key] ?? "",
    [industry],
  );
  return { t, industry };
}

/**
 * Backend round-trip for a specific business's industry. Cached aggressively
 * since this rarely changes.
 */
export function useBusinessIndustry(businessId: string | null | undefined) {
  return useQuery({
    queryKey: ["industry", "business", businessId],
    queryFn: () => industryService.getForBusiness(businessId!),
    enabled: Boolean(businessId),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Mutation: change industry on a business.
 *
 * On success:
 *   1. LIVE (C1c): write the new industryType straight into businessStore so
 *      useIndustry re-derives THIS render — no logout/login.
 *   2. Invalidate the industry + businesses queries so the server value
 *      reconciles authoritatively on the next fetch.
 */
export function useSetIndustry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      businessId,
      industryType,
    }: {
      businessId: string;
      industryType: IndustryId;
    }) => industryService.setForBusiness(businessId, industryType),
    onSuccess: (data) => {
      toast.success(`Industry set to ${INDUSTRIES[data.industryType].label}`);

      // 1. Live store write — this is what makes the change visible without a
      //    re-login. useIndustry reads businessStore, so patching the store
      //    re-derives the active industry on the next render.
      useBusinessStore
        .getState()
        .applyIndustryChange(data.businessId, data.industryType);

      // 2. Authoritative reconcile from the server on next fetch.
      queryClient.invalidateQueries({
        queryKey: ["industry", "business", data.businessId],
      });
      queryClient.invalidateQueries({ queryKey: ["businesses"] });
      queryClient.invalidateQueries({ queryKey: ["businesses", "mine"] });
    },
    onError: (err: { message?: string }) => {
      toast.error(err?.message || "Couldn't change industry. Try again.");
    },
  });
}
